import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  getNangoProviderLabel,
  NANGO_CONNECTIONS_QUERY_KEY,
  type NangoConnectSessionRequest,
} from '../../contracts';
import { NangoConnectDialog, type ConnectSessionState } from './nango-connect-dialog';
import { preloadNangoSdk, setNangoOauthPopupGate } from './open-nango-connect';
import { useMutateNangoConnectSession } from './useMutateNangoConnectSession';

interface NangoConnectButtonProps {
  allowedIntegrations?: string[];
  /** Shorter “Connect” label for catalog rows. */
  compact?: boolean;
  /** Overrides compact/`undefined` so marketplace details can use `lg`. */
  size?: ComponentProps<typeof Button>['size'];
  testId?: string;
  disabled?: boolean;
  /** Fired after sign-in succeeds (in-chat connect chips). */
  onConnected?: () => void;
  /** Fired when session creation or sign-in fails. */
  onFailed?: () => void;
  /** True while the connect dialog is open. */
  onBusyChange?: (busy: boolean) => void;
  /** Replaces the default Connect copy (and hides the Plus). */
  children?: ReactNode;
}

type ConnectLaunch = Pick<
  NangoConnectButtonProps,
  'allowedIntegrations' | 'onConnected' | 'onFailed' | 'onBusyChange'
>;

function useConnectSession() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const createSession = useMutateNangoConnectSession();
  const mutateRef = useRef(createSession.mutateAsync);
  mutateRef.current = createSession.mutateAsync;
  const [open, setOpen] = useState(false);
  const [flowId, setFlowId] = useState(0);
  const [session, setSession] = useState<ConnectSessionState>({ status: 'loading' });
  const flowIdRef = useRef(0);
  const launchRef = useRef<ConnectLaunch>({});
  const openRef = useRef(false);
  openRef.current = open;

  useEffect(() => {
    // Start the SDK chunk with the button so the confirming click stays synchronous.
    void preloadNangoSdk().catch(() => undefined);
    return () => {
      // The gate is global. Only the button whose dialog is open may disarm it.
      if (openRef.current) setNangoOauthPopupGate(false);
    };
  }, []);

  const closeDialog = useCallback(() => {
    flowIdRef.current += 1;
    setNangoOauthPopupGate(false);
    setOpen(false);
    launchRef.current.onBusyChange?.(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    // After the dialog commits. Reporting busy inside the click is the same
    // React batch as setOpen, so a parent can unmount this button before
    // the dialog paints.
    launchRef.current.onBusyChange?.(true);
  }, [open]);

  const launch = useCallback((next: ConnectLaunch) => {
    const id = flowIdRef.current + 1;
    flowIdRef.current = id;
    launchRef.current = next;
    setFlowId(id);
    setSession({ status: 'loading' });
    setOpen(true);
    // Arm before the confirming click. Awaiting this IPC there would miss
    // the user gesture and the browser would block the sign-in window.
    setNangoOauthPopupGate(true);
    void preloadNangoSdk().catch(() => undefined);

    const body: NangoConnectSessionRequest = {};
    if (next.allowedIntegrations?.length) {
      body.allowed_integrations = next.allowedIntegrations;
    }
    void mutateRef.current(body).then(
      (response) => {
        if (flowIdRef.current !== id) return;
        setSession({ status: 'ready', token: response.session_token });
      },
      (error: unknown) => {
        if (flowIdRef.current !== id) return;
        setSession({ status: 'error', error });
        launchRef.current.onFailed?.();
      }
    );
  }, []);

  const handleConnected = useCallback(
    (connectedFlowId: number, integration: string) => {
      if (flowIdRef.current !== connectedFlowId) return;
      void queryClient.invalidateQueries({ queryKey: [NANGO_CONNECTIONS_QUERY_KEY] });
      toast({
        tone: 'success',
        title: intl.formatMessage({
          defaultMessage: 'Connected',
          id: 'IvjoDSPGEQ',
        }),
        description: intl.formatMessage(
          {
            defaultMessage: '{app} was connected successfully.',
            id: 'Owh7ansf8I',
          },
          { app: getNangoProviderLabel(integration) }
        ),
      });
      launchRef.current.onConnected?.();
      closeDialog();
    },
    [closeDialog, intl, queryClient]
  );

  const handleAuthFailed = useCallback((failedFlowId: number) => {
    if (flowIdRef.current !== failedFlowId) return;
    launchRef.current.onFailed?.();
  }, []);

  return {
    open,
    flowId,
    session,
    allowedIntegrations: launchRef.current.allowedIntegrations,
    launch,
    handleConnected,
    handleAuthFailed,
    closeDialog,
  };
}

function ConnectDialog({ flow }: { flow: ReturnType<typeof useConnectSession> }) {
  return (
    <NangoConnectDialog
      open={flow.open}
      flowId={flow.flowId}
      session={flow.session}
      allowedIntegrations={flow.allowedIntegrations}
      onConnected={flow.handleConnected}
      onAuthFailed={flow.handleAuthFailed}
      onClose={flow.closeDialog}
    />
  );
}

function ConnectTrigger({
  compact = false,
  size,
  testId = 'nango-connect-button',
  disabled = false,
  children,
  onClick,
}: NangoConnectButtonProps & { onClick: () => void }) {
  const resolvedSize = size ?? (compact ? 'sm' : undefined);
  const showPlus = children == null && !compact;

  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      variant={compact && children == null ? 'secondary' : undefined}
      size={resolvedSize}
    >
      {showPlus ? <Plus className="size-4" /> : null}
      {children ??
        (compact ? (
          <FormattedMessage defaultMessage="Connect" id="+vVZ/G11Zg" />
        ) : (
          <FormattedMessage defaultMessage="Connect an app" id="vY6tjXubz1" />
        ))}
    </Button>
  );
}

export function NangoConnectButton(props: NangoConnectButtonProps) {
  const flow = useConnectSession();
  return (
    <>
      <ConnectTrigger
        {...props}
        disabled={props.disabled || flow.open}
        onClick={() => flow.launch(props)}
      />
      <ConnectDialog flow={flow} />
    </>
  );
}
